// src/pages/settings/Profile.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Toolbar,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Avatar,
  Stack,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteForever from "@mui/icons-material/DeleteForever";
import SaveIcon from "@mui/icons-material/Save";
import { getAdminProfile, updateAdminProfile } from "../../services/api";
import { saveToken } from "../../utils/auth";

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [admin, setAdmin] = useState(null);

  // form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    currentPassword: "",
  });

  // avatar handling
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // feedback
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const res = await getAdminProfile();
        const data = res?.data ?? res;
        if (!mounted) return;

        setAdmin(data);

        setForm({
          name: data?.name ?? "",
          email: data?.email ?? "",
          password: "",
          currentPassword: "",
        });

        // backend returns profileImageUrl (public URL) per controller
        setAvatarPreview(data?.profileImageUrl ?? data?.profileImage ?? null);
      } catch (err) {
        console.error("Failed to load profile", err);
        setSnackbar({ open: true, severity: "error", message: "Failed to load profile" });
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => (mounted = false);
  }, []);

  // form change
  const handleChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  // avatar select
  const handleAvatarChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    // optional client-side validation (size/type)
    if (f.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, severity: "error", message: "Image too large (max 5MB)" });
      return;
    }
    if (!f.type.match(/image\//)) {
      setSnackbar({ open: true, severity: "error", message: "Only image files allowed" });
      return;
    }

    setAvatarFile(f);
    const url = URL.createObjectURL(f);
    setAvatarPreview(url);
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    // if admin had existing avatar, allow clearing preview — user must save to persist
    setAvatarPreview(null);
  };

  // submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name?.trim() || !form.email?.trim()) {
      setSnackbar({ open: true, severity: "error", message: "Name and email are required" });
      return;
    }

    // if user provided new password, ensure currentPassword is present (frontend hint)
    if (form.password && !form.currentPassword) {
      setSnackbar({ open: true, severity: "error", message: "Enter current password to change password" });
      return;
    }

    setSaving(true);
    try {
      // assemble FormData for multipart upload
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      if (form.password) fd.append("password", form.password);
      if (form.password && form.currentPassword) fd.append("currentPassword", form.currentPassword);
      if (avatarFile) fd.append("avatar", avatarFile);

      // call API; api instance will set auth header via interceptor
      const res = await updateAdminProfile(fd);
      const payload = res?.data ?? res;

      // If backend returns token (new claims), persist it
      const token = payload?.token ?? payload?.accessToken ?? payload?.data?.token ?? null;
      if (token) {
        saveToken(token);
      }

      // backend may return updated user in payload.user or payload.user or top-level fields
      const updated = payload?.user ?? payload?.data ?? payload;
      // normalize and update local state
      if (updated) {
        setAdmin((prev) => ({ ...prev, ...updated }));
        setForm((s) => ({ ...s, password: "", currentPassword: "" }));
        setAvatarPreview(updated?.profileImageUrl ?? updated?.profileImage ?? avatarPreview);
        setAvatarFile(null);
      }

      setSnackbar({ open: true, severity: "success", message: "Profile updated successfully" });
    } catch (err) {
      console.error("update admin profile", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to update profile";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setSaving(false);
    }
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <Box>
      {/* spacer to avoid fixed header overlap */}
      <Toolbar />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Admin Profile
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {/* Left: Avatar card */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(255,255,255,0.04)",
                    textAlign: "center",
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <Avatar
                      src={avatarPreview || undefined}
                      sx={{
                        width: 96,
                        height: 96,
                        bgcolor: avatarPreview ? "transparent" : "primary.main",
                        fontSize: 36,
                      }}
                    >
                      {!avatarPreview && (form.name?.[0]?.toUpperCase() ?? "A")}
                    </Avatar>

                    <Typography variant="subtitle1" fontWeight={600}>
                      {admin?.name || "Admin User"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {admin?.email || ""}
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        component="label"
                        startIcon={<PhotoCamera />}
                        size="small"
                        sx={{ textTransform: "none" }}
                      >
                        Upload Avatar
                        <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteForever />}
                        size="small"
                        onClick={removeAvatar}
                        sx={{ textTransform: "none" }}
                      >
                        Remove
                      </Button>
                    </Stack>

                    <Divider sx={{ width: "100%", mt: 1 }} />

                    <Typography variant="caption" color="text.secondary">
                      Recommended: PNG or JPG. Max 5MB.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              {/* Right: Form */}
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(255,255,255,0.04)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Full name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant="filled"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        fullWidth
                        required
                        type="email"
                        variant="filled"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Current password"
                        name="currentPassword"
                        value={form.currentPassword}
                        onChange={handleChange}
                        fullWidth
                        type="password"
                        helperText="Required if you're changing your password"
                        variant="filled"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="New password"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        fullWidth
                        type="password"
                        helperText="Leave blank to keep current password"
                        variant="filled"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Account details
                      </Typography>

                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={saving}
                          sx={{ textTransform: "none", px: 3 }}
                        >
                          {saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
                        </Button>

                        <Button
                          variant="outlined"
                          onClick={() => {
                            // reset form to original admin values
                            setForm({
                              name: admin?.name ?? "",
                              email: admin?.email ?? "",
                              password: "",
                              currentPassword: "",
                            });
                            setAvatarPreview(admin?.profileImageUrl ?? admin?.profileImage ?? null);
                            setAvatarFile(null);
                            setSnackbar({ open: true, severity: "info", message: "Changes reverted" });
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Reset
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4500} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
